import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { ArrowLeft, Dumbbell, Repeat, FileDown } from 'lucide-react';
import { generateProgramPDF } from './generateProgramPDF';
import { toastSuccess, toastError } from './ui/toast';
import { SET_CATEGORIES, getPositionLabel, formatCategoryDisplay, getClientDescription } from './constants/blockTypes';
import ExerciseStill, { demoToOpen } from './components/ui/ExerciseStill';
import { colors as theme } from './theme';

const colors = {
  primary: theme.gold,
  primaryDark: theme.goldPressed,
  success: theme.success,
  bg: theme.bg,
  bgCard: theme.surface1,
  bgCardHover: theme.surface2,
  border: theme.borderStrong,
  text: theme.text,
  textMuted: theme.textMuted,
};

const typography = {
  fontDisplay: "'Inter', sans-serif",
  fontBody: "'Inter', sans-serif",
};

const AdminProgramViewer = ({ program, onBack }) => {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setUserId] = useState(null);

  useEffect(() => {
    loadProgramDetails();
    loadUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.id]);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) setUserId(user.id);
  };

  const loadProgramDetails = async () => {
    try {
      setLoading(true);

      // Get weeks for this program
      const { data: weeksData, error: weeksError } = await supabase
        .from('program_weeks')
        .select('*')
        .eq('program_id', program.id)
        .order('week_number', { ascending: true });

      if (weeksError) throw weeksError;

      const usesBlocks = program.uses_blocks;
      const weekIds = (weeksData || []).map(w => w.id);

      if (weekIds.length === 0) {
        setWeeks([]);
        return;
      }

      // Fetch every day, block and item for the whole program in a handful of
      // batched queries instead of firing separate blocks/items (or legacy
      // exercises) queries per day — with many days in parallel, identical-
      // shaped concurrent queries can collide on Supabase's pooled connections
      // and come back as 500s, silently leaving some days empty.
      const { data: daysData, error: daysError } = await supabase
        .from('program_days')
        .select('*')
        .in('week_id', weekIds)
        .order('day_number', { ascending: true });

      if (daysError) throw daysError;

      const dayIds = (daysData || []).map(d => d.id);
      let daysWithContent;

      if (usesBlocks) {
        const { data: blocksData } = dayIds.length
          ? await supabase.from('exercise_blocks').select('*').in('day_id', dayIds).order('block_order', { ascending: true })
          : { data: [] };

        const blockIds = (blocksData || []).map(b => b.id);
        const { data: blockItemsData } = blockIds.length
          ? await supabase.from('block_items').select('*').in('block_id', blockIds).order('position', { ascending: true })
          : { data: [] };

        const exerciseIds = [...new Set((blockItemsData || []).map(bi => bi.exercise_id))];
        let exercisesMap = {};
        if (exerciseIds.length > 0) {
          const { data: exercisesData } = await supabase.from('exercises').select('*').in('id', exerciseIds);
          (exercisesData || []).forEach(ex => { exercisesMap[ex.id] = ex; });
        }

        const blocksByDay = {};
        (blocksData || []).forEach(b => {
          if (!blocksByDay[b.day_id]) blocksByDay[b.day_id] = [];
          blocksByDay[b.day_id].push(b);
        });

        daysWithContent = (daysData || []).map(day => {
          // Attach items to blocks — blockIndex is per-day, matching the original per-day fetch
          const blocksWithItems = (blocksByDay[day.id] || []).map((block, blockIndex) => ({
            ...block,
            items: (blockItemsData || [])
              .filter(bi => bi.block_id === block.id)
              .map(bi => ({
                ...bi,
                exercise: exercisesMap[bi.exercise_id] || null,
                position_label: getPositionLabel(blockIndex, bi.position),
              })),
          }));

          return {
            ...day,
            blocks: blocksWithItems,
            exercises: [], // Keep empty for backward compat
          };
        });
      } else {
        // Legacy: fetch program_exercises
        const { data: programExercisesData, error: programExercisesError } = dayIds.length
          ? await supabase.from('program_exercises').select('*').in('day_id', dayIds).order('exercise_order', { ascending: true })
          : { data: [] };

        if (programExercisesError) throw programExercisesError;

        const exerciseIds = [...new Set((programExercisesData || []).map(pe => pe.exercise_id))];
        let exercisesMap = {};
        if (exerciseIds.length > 0) {
          const { data: exercisesData, error: exercisesError } = await supabase.from('exercises').select('*').in('id', exerciseIds);
          if (exercisesError) throw exercisesError;
          (exercisesData || []).forEach(ex => { exercisesMap[ex.id] = ex; });
        }

        const exercisesByDay = {};
        (programExercisesData || []).forEach(pe => {
          if (!exercisesByDay[pe.day_id]) exercisesByDay[pe.day_id] = [];
          exercisesByDay[pe.day_id].push({ ...pe, exercise: exercisesMap[pe.exercise_id] || null });
        });

        daysWithContent = (daysData || []).map(day => ({
          ...day,
          exercises: exercisesByDay[day.id] || [],
          blocks: [],
        }));
      }

      const daysByWeek = {};
      daysWithContent.forEach(d => {
        if (!daysByWeek[d.week_id]) daysByWeek[d.week_id] = [];
        daysByWeek[d.week_id].push(d);
      });

      const weeksWithDays = (weeksData || []).map(week => ({
        ...week,
        days: (daysByWeek[week.id] || []).sort((a, b) => a.day_number - b.day_number),
      }));

      setWeeks(weeksWithDays);
    } catch (error) {
      console.error('AdminProgramViewer: Error loading program details:', error);
      toastError('Error loading program. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const fileName = await generateProgramPDF(program, weeks, { createdAt: new Date() });
      toastSuccess(`PDF exported: ${fileName}`);
    } catch (error) {
      console.error('AdminProgramViewer: Error generating PDF:', error);
      toastError('Error generating PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        padding: '20px',
        fontFamily: typography.fontBody,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            background: colors.bgCard,
            padding: '40px',
            borderRadius: '14px',
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: `4px solid ${colors.border}`,
              borderTop: `4px solid ${colors.primary}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px',
            }} />
            <p style={{ color: colors.textMuted }}>Loading program...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: typography.fontBody,
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Back Button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <button
            onClick={onBack}
            style={{
              padding: '10px 16px',
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ArrowLeft size={18} />
            Back
          </button>

          {/* Export PDF Button */}
          <button
            onClick={handleExportPDF}
            disabled={loading || weeks.length === 0}
            style={{
              padding: '12px 20px',
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
              border: 'none',
              borderRadius: '8px',
              color: theme.bg,
              fontSize: '14px',
              fontWeight: 600,
              cursor: weeks.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: weeks.length === 0 ? 0.5 : 1,
            }}
          >
            <FileDown size={18} />
            Export PDF
          </button>
        </div>

        {/* Program Header */}
        <div style={{
          background: colors.bgCard,
          padding: '32px',
          borderRadius: '14px',
          border: `1px solid ${colors.border}`,
          marginBottom: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Dumbbell size={32} color={theme.bg} />
            </div>
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontSize: '32px',
                fontWeight: 700,
                color: colors.text,
                margin: '0 0 8px 0',
                fontFamily: typography.fontDisplay,
              }}>
                {program.name}
              </h1>
              {program.description && (
                <p style={{
                  fontSize: '16px',
                  color: colors.textMuted,
                  lineHeight: 1.6,
                  margin: '0 0 12px 0',
                }}>
                  {program.description}
                </p>
              )}
              <div style={{
                display: 'flex',
                gap: '16px',
                fontSize: '14px',
                color: colors.textMuted,
              }}>
                <span>👤 {program.user?.name || 'Unassigned'}</span>
                <span>📅 {new Date(program.created_at).toLocaleDateString()}</span>
                <span>📊 {weeks.length} week{weeks.length !== 1 ? 's' : ''}</span>
                {program.uses_blocks && (
                  <span style={{ color: colors.success }}>✓ Block-based</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Weeks and Days */}
        {weeks.map((week) => (
          <div key={week.id} style={{ marginBottom: '32px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: colors.text,
              margin: '0 0 16px 0',
              padding: '0 4px',
              fontFamily: typography.fontDisplay,
            }}>
              Week {week.week_number}
              {week.notes && (
                <span style={{ fontSize: '16px', fontWeight: 400, color: colors.textMuted, marginLeft: '12px' }}>
                  {week.notes}
                </span>
              )}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {week.days.map((day) => (
                <div
                  key={day.id}
                  style={{
                    background: colors.bgCard,
                    padding: '24px',
                    borderRadius: '14px',
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  {/* Day Header */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: colors.bg,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        color: colors.primary,
                        fontSize: '18px',
                      }}>
                        {day.day_number}
                      </div>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        color: colors.text,
                        margin: 0,
                      }}>
                        {day.name}
                      </h3>
                    </div>
                    {day.notes && (
                      <p style={{ fontSize: '14px', color: colors.textMuted, margin: '0 0 0 52px' }}>
                        {day.notes}
                      </p>
                    )}
                  </div>

                  {/* Blocks (new structure) */}
                  {day.blocks?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {day.blocks.map((block, blockIndex) => {
                        // Only treat as multi-exercise block if it's NOT a normal block
                        const isMultiExerciseBlock = block.items && block.items.length > 1 && block.set_category !== 'normal';
                        const setCategory = SET_CATEGORIES[block.set_category] || SET_CATEGORIES['normal'];

                        return (
                        <div
                          key={block.id}
                          style={{
                            background: colors.bg,
                            padding: '20px',
                            borderRadius: '14px',
                            border: `1px solid ${colors.border}`,
                          }}
                        >
                          {/* Block Header */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '16px',
                            flexWrap: 'wrap',
                          }}>
                            <span style={{
                              padding: '4px 12px',
                              background: colors.primary,
                              color: theme.bg,
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: 700,
                            }}>
                              {setCategory.icon} Block {String.fromCharCode(65 + blockIndex)}
                            </span>
                            {block.set_category && block.set_category !== 'normal' && (
                              <span style={{
                                padding: '4px 10px',
                                background: colors.bgCardHover,
                                color: colors.text,
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: 600,
                              }}>
                                {formatCategoryDisplay(block.set_category, block.category_params)}
                              </span>
                            )}
                            {block.rest_seconds && (
                              <span style={{
                                padding: '4px 10px',
                                background: colors.bgCardHover,
                                color: colors.textMuted,
                                borderRadius: '8px',
                                fontSize: '11px',
                              }}>
                                Rest: {block.rest_seconds}s {
                                  isMultiExerciseBlock
                                    ? 'after all exercises'
                                    : block.items && block.items.length > 1
                                      ? 'between sets of each exercise'
                                      : 'between sets'
                                }
                              </span>
                            )}
                          </div>

                          {/* Instructional Description for Multi-Exercise Blocks */}
                          {isMultiExerciseBlock && block.set_category !== 'normal' && (
                            <div style={{
                              background: '#FFF8E6',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              marginBottom: '16px',
                              border: '1px solid #FFE4A3',
                            }}>
                              <div style={{
                                fontSize: '13px',
                                color: '#666',
                                fontStyle: 'italic',
                                lineHeight: '1.5',
                              }}>
                                💡 <strong>How to perform:</strong> {getClientDescription(block.set_category)}
                              </div>
                            </div>
                          )}

                          {/* Block Items with Visual Connectors */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0',
                            position: 'relative',
                            paddingLeft: isMultiExerciseBlock ? '20px' : '0',
                          }}>
                            {/* Left Bracket for Multi-Exercise Blocks */}
                            {isMultiExerciseBlock && (
                              <div style={{
                                position: 'absolute',
                                left: '0',
                                top: '0',
                                bottom: '0',
                                width: '4px',
                                background: `linear-gradient(to bottom, ${colors.primary}, ${colors.primaryDark})`,
                                borderRadius: '4px',
                              }} />
                            )}

                            {block.items.map((item, itemIndex) => (
                              <React.Fragment key={item.id}>
                              <div
                                style={{
                                  background: colors.bgCard,
                                  padding: '16px',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  gap: '16px',
                                  alignItems: 'flex-start',
                                  marginBottom: itemIndex < block.items.length - 1 ? '0' : '12px',
                                  borderTopLeftRadius: itemIndex === 0 ? '10px' : '0',
                                  borderTopRightRadius: itemIndex === 0 ? '10px' : '0',
                                  borderBottomLeftRadius: itemIndex === block.items.length - 1 ? '10px' : '0',
                                  borderBottomRightRadius: itemIndex === block.items.length - 1 ? '10px' : '0',
                                }}
                              >
                                {/* Position Label */}
                                <span style={{
                                  padding: '4px 10px',
                                  background: colors.primary,
                                  color: theme.bg,
                                  borderRadius: '8px',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}>
                                  {item.position_label}
                                </span>

                                {/* Exercise GIF */}
                                {item.exercise?.gif_url && (
                                  <div style={{ flexShrink: 0 }}>
                                    <ExerciseStill
                                      gifUrl={item.exercise.gif_url}
                                      alt={item.exercise.name}
                                      onClick={() => window.open(demoToOpen(item.exercise.gif_url), '_blank')}
                                      style={{
                                        width: '100px',
                                        height: '100px',
                                        objectFit: 'contain',
                                        borderRadius: '8px',
                                        background: colors.bg,
                                        cursor: 'pointer',
                                        border: `1px solid ${colors.border}`,
                                      }}
                                    />
                                  </div>
                                )}

                                {/* Exercise Details */}
                                <div style={{ flex: 1 }}>
                                  <h5 style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: colors.text,
                                    margin: '0 0 8px 0',
                                  }}>
                                    {item.exercise?.name || 'Unknown Exercise'}
                                  </h5>

                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{
                                      padding: '4px 8px',
                                      background: colors.bg,
                                      color: colors.textMuted,
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                    }}>
                                      {item.sets} × {item.reps}
                                    </span>
                                    {item.load_percentage && (
                                      <span style={{
                                        padding: '4px 8px',
                                        background: colors.bg,
                                        color: colors.textMuted,
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                      }}>
                                        {item.load_percentage}% load
                                      </span>
                                    )}
                                  </div>

                                  {item.notes && (
                                    <div style={{
                                      marginTop: '8px',
                                      fontSize: '13px',
                                      color: colors.textMuted,
                                    }}>
                                      💡 {item.notes}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Arrow Between Exercises in Block */}
                              {isMultiExerciseBlock && itemIndex < block.items.length - 1 && (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  padding: '8px 0',
                                  background: colors.bgCard,
                                }}>
                                  <div style={{
                                    width: '0',
                                    height: '0',
                                    borderLeft: '8px solid transparent',
                                    borderRight: '8px solid transparent',
                                    borderTop: `12px solid ${colors.primary}`,
                                  }} />
                                </div>
                              )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  )}

                  {/* Legacy Exercises (old structure) */}
                  {(!day.blocks || day.blocks.length === 0) && day.exercises?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {day.exercises.map((programExercise, index) => (
                        <div
                          key={programExercise.id}
                          style={{
                            background: colors.bg,
                            padding: '20px',
                            borderRadius: '14px',
                            border: `1px solid ${colors.border}`,
                            display: 'flex',
                            gap: '20px',
                          }}
                        >
                          {/* Exercise GIF */}
                          {programExercise.exercise?.gif_url && (
                            <div style={{ flexShrink: 0 }}>
                              <ExerciseStill
                                gifUrl={programExercise.exercise.gif_url}
                                alt={programExercise.exercise.name}
                                onClick={() => window.open(demoToOpen(programExercise.exercise.gif_url), '_blank')}
                                style={{
                                  width: '140px',
                                  height: '140px',
                                  objectFit: 'contain',
                                  borderRadius: '14px',
                                  background: colors.bgCard,
                                  cursor: 'pointer',
                                  border: `2px solid ${colors.border}`,
                                  transition: 'transform 0.2s, border-color 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                  e.currentTarget.style.borderColor = colors.primary;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                  e.currentTarget.style.borderColor = colors.border;
                                }}
                              />
                            </div>
                          )}

                          {/* Exercise Details */}
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: colors.primary,
                              marginBottom: '4px',
                            }}>
                              Exercise {index + 1}
                            </div>
                            <h4 style={{
                              fontSize: '18px',
                              fontWeight: 600,
                              color: colors.text,
                              margin: '0 0 12px 0',
                            }}>
                              {programExercise.exercise?.name || 'Unknown Exercise'}
                            </h4>

                            {/* Sets & Reps */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                              <Repeat size={18} color={colors.textMuted} />
                              <span style={{ fontSize: '14px', color: colors.textMuted }}>
                                {programExercise.sets} sets × {programExercise.reps} reps
                              </span>
                            </div>

                            {/* Video Link */}
                            {programExercise.exercise?.video_url && (
                              <a
                                href={programExercise.exercise.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  padding: '8px 12px',
                                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                                  borderRadius: '8px',
                                  color: theme.bg,
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  textDecoration: 'none',
                                  marginBottom: '12px',
                                }}
                              >
                                ▶️ Watch Tutorial
                              </a>
                            )}

                            {/* Notes */}
                            {programExercise.notes && (
                              <div style={{
                                marginTop: '12px',
                                padding: '12px',
                                background: colors.bgCard,
                                borderRadius: '8px',
                                fontSize: '14px',
                                color: colors.textMuted,
                              }}>
                                💡 {programExercise.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {weeks.length === 0 && (
          <div style={{
            background: colors.bgCard,
            padding: '60px',
            borderRadius: '14px',
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
          }}>
            <p style={{ fontSize: '18px', color: colors.textMuted }}>
              This program has no weeks or exercises yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProgramViewer;
